import { useSelector } from 'react-redux'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ApplicationState } from '@/state/root.models'
import useForm from '@/hooks/form/use-form.hook'
import { FormStatusType } from '@/enums/form-status.enum'

import { authSelector } from '@/state/auth/slices/index.slices'
import { StateSchema } from '@/hooks/form/models'
import { BonusTriggerType } from '@/enums/be-bonus-trigger-type.enum'
import { PaymentMethods } from '@/enums/be-payment-methods.enum'

import { triggerSchema } from '../components/schema'
import { triggerValidator } from '../components/validator'

const useBonusTrigger = () => {
  const { t } = useTranslation()

  const { currentBonus, bonusFormMode } = useSelector(
    ({ bonusEngine }: ApplicationState) => bonusEngine,
  )

  const { brand } = useSelector(authSelector)

  const { state, handleOnChange, disable, setFormValues, setDisable } = useForm(
    triggerSchema(brand.currencies),
    triggerValidator(brand.currencies),
    () => {},
  )

  /**
   * @param reload This param is used only in the useEffect function to set again the bonus amount items
   */
  const setCurrentBonusTrigger = () => {
    const paymentMethodsList = currentBonus.trigger?.paymentMethods
      ? currentBonus.trigger?.paymentMethods
          ?.filter(
            (payment: any) => PaymentMethods[payment.payment] !== undefined,
          )
          ?.map((payment: any) => {
            return {
              index: payment.payment,
              label: payment.description,
            }
          })
      : []

    let schema: StateSchema = {}
    let currencyFlag: boolean = true

    brand.currencies.forEach((currency) => {
      const currencyIndex =
        currentBonus.trigger?.minimumDepositAmount &&
        currentBonus.trigger?.minimumDepositAmount?.find(
          (index) => index.currency === currency,
        )
      if (currencyIndex) {
        schema = {
          ...schema,
          [`minimumDepositAmount_${currency}`]: {
            value: currencyIndex.minimumDepositAmount.toString(),
            error: '',
          },
        }
      } else {
        currencyFlag =
          currentBonus.trigger?.triggerBy ===
            BonusTriggerType.DEPOSIT_BY_CODE ||
          currentBonus.trigger?.triggerBy === BonusTriggerType.DEPOSIT_BY_CLICK
            ? false
            : true
        schema = {
          ...schema,
          [`minimumDepositAmount_${currency}`]: {
            value: '',
            error:
              currentBonus.trigger?.triggerBy ===
                BonusTriggerType.DEPOSIT_BY_CODE ||
              currentBonus.trigger?.triggerBy ===
                BonusTriggerType.DEPOSIT_BY_CLICK
                ? t('error.requiredField')
                : '',
          },
        }
      }
    })

    setFormValues({
      triggerBy: {
        value: currentBonus.trigger?.triggerBy,
        error: '',
      },
      condition: {
        value:
          currentBonus.trigger?.prerequisites?.condition?.toUpperCase() || '',
        error: '',
      },
      depositCount: {
        value:
          currentBonus.trigger?.prerequisites?.depositCount.toString() || '',
        error: '',
      },
      paymentMethods: {
        value: paymentMethodsList || [],
        error: '',
      },
      ...schema,
    })

    bonusFormMode === FormStatusType.CREATE || !currencyFlag
      ? setDisable(true)
      : setDisable(false)
  }

  const handleOnChangePMethods = (name: string, data: any) => {
    handleOnChange(name, data)
  }

  useEffect(() => {
    let schema: StateSchema = {}

    if (
      state.triggerBy.value !== BonusTriggerType.DEPOSIT_BY_CODE &&
      state.triggerBy.value !== BonusTriggerType.DEPOSIT_BY_CLICK
    ) {
      brand.currencies.forEach((currency) => {
        const currencyIndex =
          currentBonus.trigger?.minimumDepositAmount &&
          currentBonus.trigger?.minimumDepositAmount?.find(
            (index) => index.currency === currency,
          )
        if (currencyIndex) {
          schema = {
            ...schema,
            [`minimumDepositAmount_${currency}`]: {
              value: currencyIndex.minimumDepositAmount,
              error: '',
            },
          }
        } else {
          schema = {
            ...schema,
            [`minimumDepositAmount_${currency}`]: {
              value: '',
              error: '',
            },
          }
        }
      })

      setFormValues({
        ...state,
        ...schema,
      })
    }
  }, [state.triggerBy.value]) // eslint-disable-line

  return {
    bonusTrigger: {
      state,
      handleOnChange,
      disable,
      setFormValues,
      setDisable,
    },
    handleOnChangePMethods,
    setCurrentBonusTrigger,
  }
}

export default useBonusTrigger
